<template>
  <Teleport to="body">
    <Transition name="command-palette">
      <div
        v-if="open"
        class="fixed inset-x-0 top-0 z-[9999] flex h-dvh md:inset-0 md:h-auto md:items-start md:justify-center md:pt-[15vh]"
        :class="{ 'safe-padding': isMobile }"
      >
        <div class="bg-obsidian-900/50 fixed inset-0 backdrop-blur-sm" @mousedown="close" />

        <div
          ref="panelRef"
          class="relative m-3 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-obsidian-200 md:m-0 md:h-auto md:max-h-[70vh] md:w-full md:max-w-xl md:flex-none"
        >
          <!-- Search input -->
          <div class="flex items-center gap-3 border-b border-obsidian-100 px-4 py-4">
            <SearchIcon class="h-5 w-5 shrink-0 text-obsidian-400" />
            <input
              ref="inputRef"
              v-model="query"
              :placeholder="placeholder"
              class="w-full bg-transparent text-base text-obsidian-900 outline-none placeholder:text-obsidian-400"
              @keydown.down.prevent="moveSelection(1)"
              @keydown.up.prevent="moveSelection(-1)"
              @keydown.enter.prevent="handleEnter"
              @keydown.escape="close"
            />
            <kbd
              class="hidden shrink-0 rounded-md border border-obsidian-200 bg-obsidian-50 px-1.5 py-0.5 text-xs font-medium text-obsidian-500 md:inline-block"
            >
              ESC
            </kbd>
            <CVButton
              variant="ghost"
              size="iconSm"
              class="shrink-0 md:hidden"
              :aria-label="t('close')"
              @click="close"
            >
              <XIcon class="size-4" />
            </CVButton>
          </div>

          <!-- Results -->
          <div
            ref="resultsRef"
            class="min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-smooth"
          >
            <!-- Loading state -->
            <div v-if="isFetching && !data" class="flex items-center justify-center py-8">
              <Spinner class="h-6 w-6 text-hazel-500" />
            </div>

            <!-- Empty query state -->
            <div
              v-else-if="!debouncedQuery"
              class="px-4 py-8 text-center text-sm text-obsidian-400"
            >
              {{ t('emptyState') }}
            </div>

            <!-- No results state -->
            <div
              v-else-if="data && totalResults === 0 && !isFetching"
              class="px-4 py-8 text-center text-sm text-obsidian-400"
            >
              {{ t('noResults') }}
            </div>

            <!-- Results list -->
            <template v-else-if="data">
              <!-- Bookings -->
              <CommandPaletteResultGroup
                :label="t('bookings')"
                :icon="CalendarIcon"
                :has-results="data.bookings.length > 0"
              >
                <CommandPaletteResultItem
                  v-for="(booking, i) in data.bookings"
                  :key="booking.id"
                  :active="selectedIndex === flatIndex('bookings', i)"
                  @activate="selectedIndex = flatIndex('bookings', i)"
                  @select="navigateTo('booking', booking.id)"
                >
                  <div class="flex min-w-0 flex-1 items-center gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <span class="font-medium">
                          <span class="text-obsidian-400">#</span>{{ booking.bookingNumber }}
                        </span>
                        <BookingStatusBadge :status="booking.status as any" size="sm" />
                      </div>
                      <div class="truncate text-xs text-obsidian-400">
                        {{ booking.customerName?.trim() || 'n/a' }}
                        <template v-if="booking.licensePlate">
                          &middot; {{ booking.licensePlate }}
                        </template>
                      </div>
                    </div>
                    <span class="shrink-0 text-xs text-obsidian-400">
                      {{ formatDate(booking.startDate) }} &ndash; {{ formatDate(booking.endDate) }}
                    </span>
                  </div>
                </CommandPaletteResultItem>
              </CommandPaletteResultGroup>

              <!-- Invoices -->
              <CommandPaletteResultGroup
                :label="t('invoices')"
                :icon="FileTextIcon"
                :has-results="data.invoices.length > 0"
              >
                <CommandPaletteResultItem
                  v-for="(invoice, i) in data.invoices"
                  :key="invoice.id"
                  :active="selectedIndex === flatIndex('invoices', i)"
                  @activate="selectedIndex = flatIndex('invoices', i)"
                  @select="navigateTo('invoice', invoice.id)"
                >
                  <div class="flex min-w-0 flex-1 items-center gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <span class="font-medium">{{ invoice.completeInvoiceNumber }}</span>
                        <InvoiceStatusBadge :status="invoice.status as InvoiceStatus" size="sm" />
                      </div>
                      <div class="truncate text-xs text-obsidian-400">
                        {{ invoice.customerName?.trim() || 'n/a' }}
                        <template v-if="invoice.bookingNumber">
                          &middot; {{ t('booking') }} #{{ invoice.bookingNumber }}
                        </template>
                      </div>
                    </div>
                  </div>
                </CommandPaletteResultItem>
              </CommandPaletteResultGroup>

              <!-- Customers -->
              <CommandPaletteResultGroup
                :label="t('customers')"
                :icon="UsersIcon"
                :has-results="data.customers.length > 0"
              >
                <CommandPaletteResultItem
                  v-for="(customer, i) in data.customers"
                  :key="customer.id"
                  :active="selectedIndex === flatIndex('customers', i)"
                  @activate="selectedIndex = flatIndex('customers', i)"
                  @select="navigateTo('customer', customer.id)"
                >
                  <div class="flex min-w-0 flex-1 items-center gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="truncate font-medium">
                        {{ customer.firstName }} {{ customer.lastName }}
                      </div>
                      <div class="truncate text-xs text-obsidian-400">
                        {{ customer.email }}
                        <span v-if="customer.company"> &middot; {{ customer.company }}</span>
                      </div>
                    </div>
                    <span class="shrink-0 text-xs text-obsidian-400">
                      #{{ customer.customerNumber }}
                    </span>
                  </div>
                </CommandPaletteResultItem>
              </CommandPaletteResultGroup>

              <!-- Cars / Vehicles -->
              <CommandPaletteResultGroup
                :label="t('vehicles')"
                :icon="CarIcon"
                :has-results="data.cars.length > 0"
              >
                <CommandPaletteResultItem
                  v-for="(car, i) in data.cars"
                  :key="car.id"
                  :active="selectedIndex === flatIndex('cars', i)"
                  @activate="selectedIndex = flatIndex('cars', i)"
                  @select="navigateTo('car', car.id)"
                >
                  <div class="flex min-w-0 flex-1 items-center gap-3">
                    <LicencePlate>{{ car.licensePlate }}</LicencePlate>
                    <div class="min-w-0 flex-1">
                      <div class="truncate font-medium">{{ car.makeName }} {{ car.model }}</div>
                      <div class="truncate text-xs text-obsidian-400">
                        {{ getCarFleetNumber(car) }}
                      </div>
                    </div>
                    <span class="shrink-0 text-xs capitalize text-obsidian-400">
                      {{ t(`carStatus.${car.status}`) }}
                    </span>
                  </div>
                </CommandPaletteResultItem>
              </CommandPaletteResultGroup>

              <!-- Subtle loading indicator for refetches -->
              <div v-if="isFetching" class="flex items-center justify-center py-2">
                <Spinner class="h-4 w-4 text-hazel-400" />
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div
            v-if="debouncedQuery && data && totalResults > 0"
            class="hidden items-center gap-4 border-t border-obsidian-100 px-4 py-2 text-xs text-obsidian-400 md:flex"
          >
            <span class="flex items-center gap-1">
              <kbd :class="kbdClasses">&uarr;</kbd>
              <kbd :class="kbdClasses">&darr;</kbd>
              {{ t('navigate') }}
            </span>
            <span class="flex items-center gap-1">
              <kbd :class="kbdClasses">&crarr;</kbd>
              {{ t('open') }}
            </span>
            <span class="flex items-center gap-1">
              <kbd :class="kbdClasses">esc</kbd>
              {{ t('close') }}
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { onKeyStroke, refDebounced, useBreakpoints } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { cvBreakpoints } from '@/utils/breakpoints';
import { useI18n } from 'vue-i18n';
import { useGlobalSearch } from '@/queries/use-search';
import { useCommandPalette } from '@/hooks/use-command-palette';
import { CalendarIcon, CarIcon, FileTextIcon, SearchIcon, UsersIcon, XIcon } from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';
import CommandPaletteResultGroup from './CommandPaletteResultGroup.vue';
import CommandPaletteResultItem from './CommandPaletteResultItem.vue';
import BookingStatusBadge from '@/components/badges/BookingStatusBadge.vue';
import InvoiceStatusBadge from '@/components/badges/InvoiceStatusBadge.vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import LicencePlate from '@/components/LicencePlate.vue';
import { format } from 'date-fns';
import type { GlobalSearchResponse } from '@/api/search.service';

const { t } = useI18n();
const {
  isOpen: open,
  canOpen,
  searchableLabels,
  openCommandPalette,
  closeCommandPalette,
} = useCommandPalette();

const placeholder = computed(() => {
  const subjects = searchableLabels.value.map((key) => t(key));
  return t('placeholder', { subjects: subjects.join(', ') });
});
const router = useRouter();

const kbdClasses =
  'rounded border border-obsidian-200 bg-obsidian-50 px-1 font-mono text-[0.625rem]';

const isMobile = useBreakpoints(cvBreakpoints).smaller('md');

const query = ref('');
const selectedIndex = ref(0);
const inputRef = ref<HTMLInputElement>();
const panelRef = ref<HTMLElement>();

const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(panelRef, {
  immediate: false,
  escapeDeactivates: false,
  allowOutsideClick: true,
  initialFocus: () => inputRef.value ?? false,
  fallbackFocus: () => panelRef.value ?? document.body,
});

const debouncedQuery = refDebounced(query, 250);

const { data, isFetching } = useGlobalSearch(debouncedQuery, open);

const categoryOrder: (keyof GlobalSearchResponse)[] = ['bookings', 'invoices', 'customers', 'cars'];

type GlobalSearchCarResult = GlobalSearchResponse['cars'][number] & { fleetNumber: number };

function getCarFleetNumber(car: GlobalSearchResponse['cars'][number]): number {
  return (car as GlobalSearchCarResult).fleetNumber;
}

const totalResults = computed(() => {
  if (!data.value) return 0;
  return categoryOrder.reduce((sum, key) => sum + data.value![key].length, 0);
});

function flatIndex(category: keyof GlobalSearchResponse, itemIndex: number): number {
  let offset = 0;
  for (const key of categoryOrder) {
    if (key === category) return offset + itemIndex;
    offset += data.value?.[key]?.length ?? 0;
  }
  return 0;
}

function getItemAtIndex(index: number): { type: string; id: string } | null {
  if (!data.value) return null;
  let offset = 0;
  for (const key of categoryOrder) {
    const items = data.value[key];
    if (index < offset + items.length) {
      return { type: key, id: items[index - offset].id };
    }
    offset += items.length;
  }
  return null;
}

function moveSelection(delta: number) {
  const total = totalResults.value;
  if (total === 0) return;
  selectedIndex.value = (selectedIndex.value + delta + total) % total;
}

function handleEnter() {
  const item = getItemAtIndex(selectedIndex.value);
  if (!item) return;

  const typeMap: Record<string, string> = {
    customers: 'customer',
    bookings: 'booking',
    invoices: 'invoice',
    cars: 'car',
  };
  navigateTo(typeMap[item.type], item.id);
}

function navigateTo(type: string, id: string) {
  close();
  const routes: Record<string, string> = {
    customer: `/user/${id}`,
    booking: `/booking-list/${id}`,
    invoice: `/invoice/${id}`,
    car: `/view-car/${id}`,
  };
  void router.push(routes[type]);
}

function close() {
  closeCommandPalette();
  query.value = '';
  selectedIndex.value = 0;
}

function formatDate(date: string | Date): string {
  try {
    return format(date instanceof Date ? date : new Date(date), 'dd.MM.yy');
  } catch {
    return String(date);
  }
}

watch(debouncedQuery, () => {
  selectedIndex.value = 0;
});

watch([open, isMobile], ([isOpen, mobile]) => {
  document.documentElement.classList.toggle('overflow-hidden', Boolean(isOpen && mobile));
});

watch(open, (isOpen) => {
  if (!isOpen) {
    deactivateFocusTrap();
    return;
  }
  void nextTick(() => {
    activateFocusTrap();
    inputRef.value?.focus({ preventScroll: true });
  });
});

watch(canOpen, (allowed) => {
  if (!allowed && open.value) close();
});

onBeforeUnmount(() => {
  deactivateFocusTrap();
  document.documentElement.classList.remove('overflow-hidden');
});

onKeyStroke('k', (e) => {
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    if (open.value) {
      close();
    } else {
      openCommandPalette();
    }
  }
});

onKeyStroke('Escape', () => {
  if (open.value) close();
});

defineExpose({ open: openCommandPalette });
</script>

<style scoped>
.command-palette-enter-active,
.command-palette-leave-active {
  transition: all 0.2s ease;
}

.command-palette-enter-active > div:last-child,
.command-palette-leave-active > div:last-child {
  transition: all 0.2s ease;
}

.command-palette-enter-from,
.command-palette-leave-to {
  opacity: 0;
}

.command-palette-enter-from > div:last-child,
.command-palette-leave-to > div:last-child {
  transform: scale(0.96) translateY(-8px);
  opacity: 0;
}
</style>

<i18n lang="json">
{
  "en": {
    "placeholder": "Search {subjects}...",
    "emptyState": "Start typing to search across all entities",
    "noResults": "No results found",
    "customers": "Customers",
    "bookings": "Bookings",
    "invoices": "Invoices",
    "vehicles": "Vehicles",
    "booking": "Booking",
    "navigate": "Navigate",
    "open": "Open",
    "close": "Close"
  },
  "de": {
    "placeholder": "{subjects} suchen...",
    "emptyState": "Tippen Sie, um in allen Bereichen zu suchen",
    "noResults": "Keine Ergebnisse gefunden",
    "customers": "Kunden",
    "bookings": "Buchungen",
    "invoices": "Rechnungen",
    "vehicles": "Fahrzeuge",
    "booking": "Buchung",
    "navigate": "Navigieren",
    "open": "Öffnen",
    "close": "Schließen"
  },
  "es": {
    "placeholder": "Buscar {subjects}...",
    "emptyState": "Escribe para buscar en todas las entidades",
    "noResults": "No se encontraron resultados",
    "customers": "Clientes",
    "bookings": "Reservas",
    "invoices": "Facturas",
    "vehicles": "Vehículos",
    "booking": "Reserva",
    "navigate": "Navegar",
    "open": "Abrir",
    "close": "Cerrar"
  }
}
</i18n>
